import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, Alert, Platform, Switch, Linking } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useRouter, useFocusEffect, useLocalSearchParams } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import theme from '../../../../config/theme';
import { databaseService } from '../../../../services/databaseService';
import { APP_CONSTANTS } from '../../../../config/constants';
import { observer } from '@legendapp/state/react';
import { globalState } from '../../../../state/globalState';
import { v4 as uuidv4 } from 'uuid';
import { useWebAlert } from '../../../../components/WebAlert';
import PromptDialog from '../../../../components/PromptDialog';
import { SettingsChecklistsLogger } from '../../../../utils/logger';
import CampoMarcacaoBackgroundButton from '../../../../components/checklist/CampoMarcacaoBackgroundButton';
import { assertStructuralOnline } from '../../../../services/checklist/ChecklistStructuralOfflineGate';
import { checklistStructuralMutationService } from '../../../../services/checklist/ChecklistStructuralMutationService';
import {
  getSettingsContentPaddingBottom,
  getSettingsHeaderPaddingTop,
} from '@/utils/safeArea';
import { isIOS } from '@/utils/platform';
import { canCreateGpsAddressField } from '@/services/checklist/gpsAddressCapability';
import { useGpsAddressCapability } from '@/hooks/useGpsAddressCapability';

interface CampoTemplate {
  id: string;
  grupo: string;
  nome: string;
  tipo: string;
  endereco_gps?: number | boolean | null;
  visivel: number; // 0 | 1
  deletavel: number; // 0 | 1
  alteravel: number; // 0 | 1
  ordem: number; // Sequência de ordenação (1,2,3,...)
  backgroundUrl?: string | null; // url da imagem de fundo (para campos de marcação)
  retired_at?: string | null;
}

const FieldsConfigScreen = observer(() => {
  const router = useRouter();
  const safeAreaInsets = useSafeAreaInsets();
  const headerPaddingTop = getSettingsHeaderPaddingTop(safeAreaInsets.top);
  const scrollViewBottomPadding = getSettingsContentPaddingBottom(safeAreaInsets.bottom);
  const [campos, setCampos] = useState<CampoTemplate[]>([]);
  const [templateId, setTemplateId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const { showAlert } = useWebAlert();

  // Estado para dialog
  const [dialogVisible, setDialogVisible] = useState(false);
  const [dialogMode, setDialogMode] = useState<'group' | 'field' | 'editField' | 'editGroup' | null>(null);
  const [dialogGrupo, setDialogGrupo] = useState<string>('');
  const [campoEditando, setCampoEditando] = useState<CampoTemplate | null>(null);

  const params = useLocalSearchParams<{ templateId?: string }>();
  const paramTemplateId = params.templateId as string | undefined;

  const empresaIdGlobal = globalState.company.data.get()?.id || globalState.user.profile.get()?.empresa_id || '';
  const [empresaIdTemplate, setEmpresaIdTemplate] = useState<string>('');
  const isIOSCompanionMode = isIOS;

  const empresaId = empresaIdGlobal || empresaIdTemplate;
  const gpsEnabled = useGpsAddressCapability(empresaId);

  // Helpers para alertas cross-platform
  const showMessage = (title: string, msg: string, buttons?: any[]) => {
    if (Platform.OS !== 'android') {
      showAlert(title, msg, buttons ?? [{ text: 'OK' }]);
    } else {
      Alert.alert(title, msg, buttons);
    }
  };

  // URL de contato via WhatsApp vindo das constantes globais
  const handleWhatsApp = () => {
    Linking.openURL(APP_CONSTANTS.CONTACTS.WHATSAPP_URL);
  };

  /**
   * Calcula o próximo valor de ordem disponível (maior + 1). Se não houver itens, retorna 1.
   */
  const getNextOrder = useCallback((): number => {
    if (campos.length === 0) return 1;
    return Math.max(...campos.map(c => c.ordem ?? 0)) + 1;
  }, [campos]);

  /**
   * Recalcula a coluna `ordem` sequencialmente para a lista recebida
   * e persiste apenas os registros que mudaram.
   */
  const recomputeOrders = useCallback(async (lista: CampoTemplate[]) => {
    const gate = assertStructuralOnline('field', 'reorder');
    if (!gate.allowed) {
      showMessage(gate.title, gate.message);
      return;
    }

    // Ordena por ordem atual asc para manter consistência, fallback alfabética
    const ordered = [...lista].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0) || a.nome.localeCompare(b.nome));
    const updates: Array<{ id: string; updates: Record<string, unknown> }> = [];
    const orderedWithNewOrders = ordered.map((campo, idx) => {
      const novaOrdem = idx + 1;
      if (campo.ordem !== novaOrdem) {
        updates.push({ id: campo.id, updates: { ordem: novaOrdem } });
      }
      return { ...campo, ordem: novaOrdem };
    });

    if (updates.length) {
      SettingsChecklistsLogger.Main.info(`[recomputeOrders] ${updates.length} itens com ordem alterada – persistindo`);
      await checklistStructuralMutationService.reorderFields(templateId, updates);
    }

    // Atualiza o estado local garantindo a ordem correta
    setCampos(orderedWithNewOrders);
  }, [setCampos, templateId]);

  const loadCampos = async () => {
    try {
      setIsLoading(true);
      setError(null);

      SettingsChecklistsLogger.Main.info('loadCampos - iniciando carregamento');

      // 1) Descobrir template ativo ou usar parametro
      let templateAtualId: string | undefined = paramTemplateId;
      if (!templateAtualId) {
        const templates = await databaseService.listItems<any>(APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME, { empresa_id: empresaId });
        const firstTpl = templates.find(template => !template.retired_at);
        if (!firstTpl) {
          throw new Error('Nenhum template encontrado para a empresa.');
        }
        templateAtualId = firstTpl.id;
      }

      // Carregar campos do template escolhido
      const camposTemplate = await databaseService.listItems<CampoTemplate>(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME, { template_id: templateAtualId });
      // Carregar imagens de background existentes para campos de marcação
      const images = await databaseService.listItems<any>(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_MARCACAO_IMAGENS.NAME, { template_id: templateAtualId });
      const imagesMap: Record<string, string> = {};
      images.forEach(img => {
        imagesMap[img.campo_id] = img.foto_url;
      });

      const enrichedCampos = camposTemplate
        .filter(campo => !campo.retired_at)
        .map(c => ({ ...c, backgroundUrl: imagesMap[c.id] || null }));
      setTemplateId(templateAtualId!);
      enrichedCampos.sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
      setCampos(enrichedCampos);
      SettingsChecklistsLogger.Fields.info(`[loadCampos] templateId=${templateAtualId} campos=${enrichedCampos.length}`);

      // Descobre empresaId a partir do template selecionado se não veio do estado global
      if (!empresaIdGlobal && templateAtualId) {
        try {
          const templateRow = await databaseService.readItem<any>(APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME, templateAtualId);
          if (templateRow?.empresa_id) {
            setEmpresaIdTemplate(templateRow.empresa_id);
          }
        } catch { }
      }
    } catch (err: any) {
      console.error(err);
      SettingsChecklistsLogger.Main.info('loadCampos - erro: ' + (err?.message || err));
      setError(err?.message || 'Erro ao carregar campos.');
    } finally {
      setIsLoading(false);
    }
  };

  useFocusEffect(
    useCallback(() => {
      loadCampos();
    }, [])
  );

  // Toggle visibilidade
  const handleToggleVisibility = async (campo: CampoTemplate) => {
    if (!campo.alteravel) return;
    const gate = assertStructuralOnline('field', 'edit');
    if (!gate.allowed) {
      showMessage(gate.title, gate.message);
      return;
    }

    SettingsChecklistsLogger.Main.info(`Toggling visibilidade campo ${campo.nome} (${campo.id}) de ${campo.visivel} para ${campo.visivel ? 0 : 1}`);
    try {
      await checklistStructuralMutationService.updateField(
        templateId,
        campo.id,
        { visivel: campo.visivel ? 0 : 1 }
      );
      setCampos(prev => prev.map(c => (c.id === campo.id ? { ...c, visivel: campo.visivel ? 0 : 1 } : c)));
    } catch (err: any) {
      showMessage('Erro', err?.message || 'Não foi possível atualizar o campo');
    }
  };

  // Delete campo
  const handleDeleteCampo = (campo: CampoTemplate) => {
    if (!campo.deletavel) return;
    SettingsChecklistsLogger.Main.info(`Solicitando exclusão do campo ${campo.nome} (${campo.id})`);
    showMessage(
      'Excluir Campo',
      `Deseja excluir o campo "${campo.nome}"? O campo deixará de aparecer nos novos checklists. As respostas e imagens dos checklists anteriores serão preservadas.`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Excluir', style: 'destructive', onPress: async () => {
            const gate = assertStructuralOnline('field', 'retire');
            if (!gate.allowed) {
              showMessage(gate.title, gate.message);
              return;
            }

            try {
              const retiredAt = new Date().toISOString();
              await checklistStructuralMutationService.retireField(templateId, campo.id, retiredAt);

              const novaLista = campos.filter(c => c.id !== campo.id);
              await recomputeOrders(novaLista);
            } catch (err: any) {
              showMessage('Erro', err?.message || 'Não foi possível excluir o campo');
            }
          }
        }
      ]
    );
  };

  // Lista completa de tipos disponíveis
  const FIELD_TYPES_BASE = [
    { value: 'text', label: 'Texto' },
    ...(gpsEnabled ? [{ value: 'endereco_gps', label: 'Endereço GPS' }] : []),
    { value: 'foto', label: 'Foto' },
    { value: 'assinatura', label: 'Assinatura' },
    { value: 'marcação', label: 'Marcação' },
    { value: 'endereco_completo', label: 'Endereço Completo (CEP, end, bairro, cidade, UF)' },
    { value: 'dropdown1', label: 'Nível de Combustível (Vazio, 1/4, 2/4, 3/4, Cheio)' },
    { value: 'dropdown2', label: '(Bom, Médio, Ruim)' },
    { value: 'dropdown3', label: '(Bom, Danificado, Ausente)' },
    { value: 'dropdown4', label: 'OK / Não OK' },
    { value: 'dropdown5', label: 'Sim / Não' },
    { value: 'timestamp with time zone', label: 'Data/Hora' },
    { value: 'number', label: 'Número' },
    { value: 'mensagem pronta', label: 'Mensagem Pronta' },
  ];

  // ======= Lógica de limite de fotos por plano =======
  // Valor máximo permitido para a empresa (default 5)
  const maxFotosEmpresa = globalState.company.data.get()?.num_fotos ?? 5;

  // Quantidade atual de campos do tipo foto no template carregado
  const fotosAtuais = campos.filter(c => c.tipo === 'foto').length;

  // Se já atingiu o limite, remove "foto" das opções do dialog
  const FIELD_TYPES = useMemo(() => {
    if (fotosAtuais >= maxFotosEmpresa) {
      return FIELD_TYPES_BASE.filter(t => t.value !== 'foto');
    }
    return FIELD_TYPES_BASE;
  }, [fotosAtuais, maxFotosEmpresa, gpsEnabled]);

  // Create novo grupo
  const handleAddGroup = () => {
    SettingsChecklistsLogger.Main.info('handleAddGroup - abrir dialog');
    setDialogMode('group');
    setDialogVisible(true);
  };

  // Add campo dentro de grupo
  const handleAddCampo = (grupo: string) => {
    SettingsChecklistsLogger.Main.info(`handleAddCampo - abrir dialog para grupo ${grupo}`);
    setDialogGrupo(grupo);
    setDialogMode('field');
    setDialogVisible(true);
  };

  // Editar campo existente (renomear)
  const handleEditCampo = (campo: CampoTemplate) => {
    SettingsChecklistsLogger.Main.info(`handleEditCampo - abrir dialog para campo ${campo.id}`);
    setCampoEditando(campo);
    setDialogGrupo(campo.grupo);
    setDialogMode('editField');
    setDialogVisible(true);
  };

  // Editar grupo existente (renomear)
  const handleEditGrupo = (grupo: string) => {
    SettingsChecklistsLogger.Main.info(`handleEditGrupo - abrir dialog para grupo ${grupo}`);
    setDialogGrupo(grupo);
    setDialogMode('editGroup');
    setDialogVisible(true);
  };

  const onDialogCancel = () => {
    SettingsChecklistsLogger.Main.info('PromptDialog cancelado');
    setDialogVisible(false);
  };

  const onDialogSubmit = async (valor: string, tipo?: string) => {
    setDialogVisible(false);
    const name = valor.trim();
    if (!name) {
      const titulo = dialogMode === 'editField' || dialogMode === 'field' ? 'Nome do campo inválido' : 'Nome do grupo inválido';
      showMessage(titulo, 'Informe um nome válido para continuar.');
      return;
    }

    SettingsChecklistsLogger.Main.info(`[onDialogSubmit] dialogMode=${dialogMode} valor='${name}' tipo=${tipo} empresaId=${empresaId}`);

    if (dialogMode === 'group') {
      // Ao criar um novo grupo solicitamos imediatamente a criação do primeiro campo
      SettingsChecklistsLogger.Main.info(`Novo grupo criado: ${name} – solicitando primeiro campo`);
      setExpandedGroup(name);

      // Abre o prompt de "Novo Campo" já com o grupo pré-selecionado
      // Isso garante que o grupo só será efetivamente persistido
      // quando ao menos um campo for criado, evitando grupos "fantasmas"
      setTimeout(() => {
        handleAddCampo(name);
      }, 0);

      return;
    }

    if (dialogMode === 'editField' && campoEditando) {
      const gate = assertStructuralOnline('field', 'edit');
      if (!gate.allowed) {
        showMessage(gate.title, gate.message);
        return;
      }

      // Renomear campo existente
      SettingsChecklistsLogger.Main.info(`[onDialogSubmit] Renomeando campo ${campoEditando.id} para '${valor}'`);
      try {
        await checklistStructuralMutationService.updateField(
          templateId,
          campoEditando.id,
          { nome: valor }
        );
        setCampos(prev => prev.map(c => c.id === campoEditando.id ? { ...c, nome: valor } : c));
      } catch (err: any) {
        showMessage('Erro', err?.message || 'Não foi possível renomear o campo');
      } finally {
        setCampoEditando(null);
      }
      return;
    }

    if (dialogMode === 'editGroup') {
      const grupoAntigo = dialogGrupo;
      if (!grupoAntigo) return;
      if (grupoAntigo === name) return;

      const gruposExistentes = Array.from(new Set(campos.map(c => c.grupo).filter(Boolean)));
      const jaExiste = gruposExistentes.some(g => g === name);

      const renomearGrupo = async () => {
        const gate = assertStructuralOnline('field', 'edit');
        if (!gate.allowed) {
          showMessage(gate.title, gate.message);
          return;
        }

        try {
          const camposDoGrupo = campos.filter(c => c.grupo === grupoAntigo);
          SettingsChecklistsLogger.Main.info(`[onDialogSubmit] Renomeando grupo "${grupoAntigo}" (${camposDoGrupo.length} campos) para "${name}"`);

          await checklistStructuralMutationService.updateFields(
            templateId,
            camposDoGrupo.map(campo => ({ id: campo.id, updates: { grupo: name } }))
          );

          setCampos(prev => prev.map(c => c.grupo === grupoAntigo ? { ...c, grupo: name } : c));
          setExpandedGroup(prev => prev === grupoAntigo ? name : prev);
        } catch (err: any) {
          showMessage('Erro', err?.message || 'Não foi possível renomear o grupo');
        }
      };

      if (jaExiste) {
        showMessage(
          'Mesclar grupos?',
          `Já existe um grupo chamado "${name}". Se continuar, todos os campos do grupo "${grupoAntigo}" serão movidos para "${name}".`,
          [
            { text: 'Cancelar', style: 'cancel' },
            { text: 'Continuar', style: 'destructive', onPress: renomearGrupo },
          ]
        );
      } else {
        await renomearGrupo();
      }
      return;
    }

    if (dialogMode === 'field') {
      const gate = assertStructuralOnline('field', 'create');
      if (!gate.allowed) {
        showMessage(gate.title, gate.message);
        return;
      }

      SettingsChecklistsLogger.Main.info(`Novo campo em ${dialogGrupo} - nome: ${name} tipo: ${tipo}`);
      const selectedType = tipo || 'text';
      if (selectedType === 'endereco_gps' && (!gpsEnabled || !await canCreateGpsAddressField(empresaId))) {
        showMessage('Recurso indisponível', 'O endereço GPS não está habilitado para esta empresa.');
        return;
      }

      // Validação de limite de fotos
      if (selectedType === 'foto') {
        const fotosCount = campos.filter(c => c.tipo === 'foto').length;
        if (fotosCount >= maxFotosEmpresa) {
          showMessage(
            'Limite de fotos atingido',
            isIOSCompanionMode
              ? `Este checklist já possui ${fotosCount} ${fotosCount === 1 ? 'campo' : 'campos'} de foto e o limite atual da sua conta é ${maxFotosEmpresa}.\n\nSe você acredita que sua empresa precisa desse acesso, fale com o suporte para verificarmos o cadastro.`
              : `Este checklist já possui ${fotosCount} ${fotosCount === 1 ? 'campo' : 'campos'} de foto e o limite do seu plano é ${maxFotosEmpresa}.\n\nPrecisa de mais campos? Fale conosco via WhatsApp!`,
            isIOSCompanionMode
              ? [
                  { text: 'Fechar', style: 'cancel' },
                  { text: 'Suporte', onPress: handleWhatsApp }
                ]
              : [
                  { text: 'Cancelar', style: 'cancel' },
                  { text: 'WhatsApp', onPress: handleWhatsApp }
                ]
          );
          return;
        }
      }

      try {
        const templateIdAtual = templateId;
        const now = Date.now();
        if (selectedType === 'endereco_completo') {
          // Cria 6 campos de endereço
          const enderecoCampos = [
            { nome: 'CEP', tipo: 'cep1' },
            { nome: 'Endereço', tipo: 'cep2' },
            { nome: 'Número', tipo: 'cep3' },
            { nome: 'Bairro', tipo: 'cep4' },
            { nome: 'Cidade', tipo: 'cep5' },
            { nome: 'UF', tipo: 'cep6' },
          ];
          const novosCampos: CampoTemplate[] = [];
          const novosCamposPayload: Record<string, unknown>[] = [];
          let ordemAtual = getNextOrder();
          for (const campo of enderecoCampos) {
            const newId = uuidv4();
            const fieldPayload = {
              id: newId,
              empresa_id: empresaId,
              template_id: templateIdAtual,
              versao: 1,
              grupo: dialogGrupo,
              nome: campo.nome,
              tipo: campo.tipo,
              obrigatorio: 0,
              deletavel: 1,
              alteravel: 1,
              visivel: 1,
              ordem: ordemAtual,
              created_at: now,
              updated_at: now,
            };
            novosCamposPayload.push(fieldPayload);
            novosCampos.push({
              id: newId,
              grupo: dialogGrupo,
              nome: campo.nome,
              tipo: campo.tipo,
              visivel: 1,
              deletavel: 1,
              alteravel: 1,
              ordem: ordemAtual,
            });
            ordemAtual += 1;
          }
          await checklistStructuralMutationService.createFields(templateIdAtual, novosCamposPayload);
          const listaAtualizada = [...campos, ...novosCampos];
          await recomputeOrders(listaAtualizada); // garante sequência única
          SettingsChecklistsLogger.Main.info(`Campos de endereço criados com sucesso`);
        } else {
          const newId = uuidv4();
          const ordemNova = getNextOrder();
          await checklistStructuralMutationService.createFields(templateIdAtual, [{
            id: newId,
            empresa_id: empresaId,
            template_id: templateIdAtual,
            versao: 1,
            grupo: dialogGrupo,
            nome: name,
            tipo: selectedType === 'endereco_gps' ? 'text' : selectedType,
            ...(selectedType === 'endereco_gps' ? { endereco_gps: 1 } : {}),
            obrigatorio: 0,
            deletavel: 1,
            alteravel: 1,
            visivel: 1,
            ordem: ordemNova,
            created_at: now,
            updated_at: now,
          }]);
          const listaAtualizada: CampoTemplate[] = [...campos, {
            id: newId,
            grupo: dialogGrupo,
            nome: name,
            tipo: selectedType === 'endereco_gps' ? 'text' : selectedType,
            ...(selectedType === 'endereco_gps' ? { endereco_gps: 1 } : {}),
            visivel: 1,
            deletavel: 1,
            alteravel: 1,
            ordem: ordemNova,
          }];
          await recomputeOrders(listaAtualizada);
          SettingsChecklistsLogger.Main.info(`Campo ${name} criado com sucesso`);
        }
      } catch (err: any) {
        showMessage('Erro', err?.message || 'Não foi possível criar o campo');
      }
    }
  };

  const groupedCampos = campos.reduce<Record<string, CampoTemplate[]>>((acc, campo) => {
    if (!acc[campo.grupo]) acc[campo.grupo] = [];
    acc[campo.grupo].push(campo);
    return acc;
  }, {});

  const renderCampo = (campo: CampoTemplate) => (
    SettingsChecklistsLogger.Fields.info(`[renderCampo] id=${campo.id} tipo=${campo.tipo} visivel=${campo.visivel} deletavel=${campo.deletavel} alteravel=${campo.alteravel}`),
    <View key={campo.id} style={styles.campoItem}>
      <Text style={styles.campoNome}>{campo.nome}</Text>
      <View style={styles.campoActions}>
        {campo.alteravel ? (
          <Switch
            value={!!campo.visivel}
            onValueChange={() => handleToggleVisibility(campo)}
            thumbColor={theme.colors.primary.default}
          />
        ) : null}
        {campo.alteravel ? (
          <TouchableOpacity style={{ marginLeft: 8 }} onPress={() => handleEditCampo(campo)}>
            <MaterialCommunityIcons name="pencil" size={20} color={theme.colors.text.secondary} />
          </TouchableOpacity>
        ) : null}
        {(() => {
          const strictEq = campo.tipo === 'marcação';
          const looseEq = (campo.tipo as any) == 'marcação';
          SettingsChecklistsLogger.Fields.info(`[renderCampo] comparação campo.tipo (${campo.tipo}) === 'marcação' => ${strictEq} | == => ${looseEq}`);
          return strictEq;
        })() ? (
          <CampoMarcacaoBackgroundButton
            campo={campo}
            empresaId={empresaId}
            templateId={templateId}
            onBackgroundUpdated={(campoId: string, url: string | null) => setCampos(prev => prev.map(c => c.id === campoId ? { ...c, backgroundUrl: url } : c))}
          />
        ) : null}
        {campo.deletavel ? (
          <TouchableOpacity accessibilityRole="button" accessibilityLabel={`Excluir campo ${campo.nome}`} style={{ marginLeft: 8 }} onPress={() => handleDeleteCampo(campo)}>
            <MaterialCommunityIcons name="trash-can-outline" size={20} color={theme.colors.util.error} />
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
  );

  const renderGrupo = (grupo: string, itens: CampoTemplate[]) => {
    const isExpanded = expandedGroup === grupo;
    return (
      <View key={grupo} style={styles.groupContainer}>
        <TouchableOpacity style={styles.groupHeader} onPress={() => setExpandedGroup(isExpanded ? null : grupo)}>
          <Text style={styles.groupTitle}>{grupo}</Text>
          <View style={styles.groupActions}>
            <View style={styles.groupActionItem}>
              <TouchableOpacity onPress={() => handleEditGrupo(grupo)} style={styles.groupActionButton}>
                <MaterialCommunityIcons name="pencil" size={20} color={theme.colors.text.secondary} />
              </TouchableOpacity>
              <Text style={styles.groupActionLabel}>Editar{'\n'}Grupo</Text>
            </View>
            <View style={styles.groupActionItem}>
              <TouchableOpacity onPress={() => handleAddCampo(grupo)} style={styles.groupActionButton}>
                <MaterialCommunityIcons name="plus" size={20} color={theme.colors.primary.default} />
              </TouchableOpacity>
              <Text style={styles.groupActionLabel}>Novo{'\n'}Campo</Text>
            </View>
            <View style={styles.groupActionItem}>
              <MaterialCommunityIcons name={isExpanded ? 'chevron-up' : 'chevron-down'} size={24} color={theme.colors.text.primary} />
              <Text style={styles.groupActionLabel}>{isExpanded ? 'Recolher' : 'Expandir'}</Text>
            </View>
          </View>
        </TouchableOpacity>
        {isExpanded && <View style={styles.camposContainer}>{itens.sort((a, b) => a.nome.localeCompare(b.nome)).map(renderCampo)}</View>}
      </View>
    );
  };

  const goBack = () => router.back();

  const renderContent = () => {
    if (isLoading) {
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary.default} />
          <Text style={styles.loadingText}>Carregando campos...</Text>
        </View>
      );
    }
    if (error) {
      return (
        <View style={styles.centerContainer}>
          <MaterialCommunityIcons name="alert-circle-outline" size={48} color={theme.colors.util.error} />
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity style={styles.retryButton} onPress={loadCampos}>
            <Text style={styles.retryButtonText}>Tentar novamente</Text>
          </TouchableOpacity>
        </View>
      );
    }

    const gruposOrdenados = Object.keys(groupedCampos).sort();

    return (
      <ScrollView contentContainerStyle={{ paddingBottom: scrollViewBottomPadding }}>
        {/* Indicador de uso de campos de foto */}
        <View style={styles.limitInfo}>
          <MaterialCommunityIcons name="camera" size={16} color={theme.colors.text.secondary} />
          <Text style={styles.limitInfoText}> {fotosAtuais}/{maxFotosEmpresa} campos de foto </Text>
          <TouchableOpacity style={styles.whatsAppButton} onPress={handleWhatsApp}>
            <MaterialCommunityIcons name="whatsapp" size={16} color="#25D366" />
            <Text style={styles.whatsAppText}>{isIOSCompanionMode ? ' Ajuda' : ' Solicitar mais'}</Text>
          </TouchableOpacity>
        </View>
        {gruposOrdenados.map(grupo => renderGrupo(grupo, groupedCampos[grupo]))}
        {/* Botão para adicionar grupo */}
        <TouchableOpacity style={styles.addGroupButton} onPress={handleAddGroup}>
          <MaterialCommunityIcons name="plus" size={20} color={theme.colors.primary.default} />
          <Text style={styles.addGroupText}>Adicionar Grupo</Text>
        </TouchableOpacity>
      </ScrollView>
    );
  };

  // JSX do dialog
  const renderDialog = () => (
    <PromptDialog
      visible={dialogVisible}
      title={
        dialogMode === 'group'
          ? 'Novo Grupo'
          : dialogMode === 'field'
            ? 'Novo Campo'
            : dialogMode === 'editField'
              ? 'Renomear Campo'
              : 'Renomear Grupo'
      }
      message={
        dialogMode === 'group'
          ? 'Digite o nome do novo grupo.'
          : dialogMode === 'field'
            ? 'Escolha o tipo do campo'
            : dialogMode === 'editField'
              ? 'Digite o novo nome do campo:'
              : 'Digite o novo nome do grupo:'
      }
      placeholder={dialogMode === 'group' || dialogMode === 'editGroup' ? 'Nome do grupo' : 'Nome do campo'}
      initialValue={
        dialogMode === 'editField' && campoEditando
          ? campoEditando.nome
          : dialogMode === 'editGroup'
            ? dialogGrupo
            : undefined
      }
      confirmLabel={dialogMode === 'editField' || dialogMode === 'editGroup' ? 'Salvar' : undefined}
      types={dialogMode === 'field' ? FIELD_TYPES : []}
      onCancel={onDialogCancel}
      onSubmit={onDialogSubmit}
    />
  );

  return (
    <View style={styles.container}>
      <View style={[styles.header, { paddingTop: headerPaddingTop }]}>
        <TouchableOpacity style={styles.backButton} onPress={goBack}>
          <MaterialCommunityIcons name="arrow-left" size={24} color={theme.colors.text.inverse} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Configurar Campos</Text>
        <View style={{ width: 40 }} />
      </View>
      {renderContent()}
      {renderDialog()}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  header: {
    backgroundColor: theme.colors.primary.default,
    padding: theme.spacing.lg,
    paddingTop: Platform.OS === 'ios' ? theme.spacing.xl + theme.spacing.sm : theme.spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    width: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    flex: 1,
    fontSize: theme.fontSizes.xl,
    color: theme.colors.text.inverse,
    fontWeight: theme.fontWeights.bold as any,
    textAlign: 'center',
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.lg,
  },
  loadingText: {
    marginTop: theme.spacing.md,
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.secondary,
  },
  errorText: {
    marginTop: theme.spacing.md,
    fontSize: theme.fontSizes.md,
    color: theme.colors.util.error,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: theme.spacing.md,
    backgroundColor: theme.colors.primary.default,
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.sm,
  },
  retryButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.md,
  },
  groupContainer: {
    backgroundColor: theme.colors.background.primary,
    margin: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    ...theme.shadows.sm,
  },
  groupHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border.light,
  },
  groupActions: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: theme.spacing.sm,
  },
  groupActionItem: {
    alignItems: 'center',
    maxWidth: 92,
  },
  groupActionButton: {
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  groupActionLabel: {
    marginTop: 2,
    fontSize: theme.fontSizes.xs - 1,
    color: theme.colors.text.secondary,
    textAlign: 'center',
  },
  groupTitle: {
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
  },
  camposContainer: {
    paddingHorizontal: theme.spacing.lg,
    paddingBottom: theme.spacing.md,
  },
  campoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginVertical: 6,
  },
  campoNome: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
  },
  campoActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  addGroupButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    marginTop: theme.spacing.md,
  },
  addGroupText: {
    marginLeft: 4,
    fontSize: theme.fontSizes.md,
    color: theme.colors.primary.default,
  },
  limitInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing.sm,
    marginBottom: theme.spacing.sm,
    flexWrap: 'wrap',
  },
  limitInfoText: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.xs,
  },
  whatsAppButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: theme.spacing.xs,
    marginTop: Platform.OS === 'web' ? 0 : theme.spacing.xs, // quebra para mobile se necessário
  },
  whatsAppText: {
    fontSize: theme.fontSizes.xs,
    color: '#25D366',
  },
});

export default FieldsConfigScreen; 
